import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    // GEOMETRY IS SET BEFORE super.connectedCallback() ON PURPOSE. Do not
    // reorder this, and do not defer it to rAF, a microtask, or anything else.
    //
    // snippets/fly-to-cart-styles.liquid gives this element an unconditional
    // `animation-name: travel-x, travel-y, travel-scale` plus
    // `translate: var(--start-x, 0) var(--start-y, 0)`. The animation therefore
    // begins at the FIRST style recalculation after the element is inserted,
    // using whatever those custom properties are at that instant. If they are
    // still unset, the fallbacks are 0 0, which is the viewport's top-left
    // corner.
    //
    // Two earlier attempts at this fix both failed on exactly that:
    //   1. Deferring the measurement by one requestAnimationFrame - the whole
    //      first frame animated from the corner. May: "it is still flying to
    //      the left".
    //   2. Measuring synchronously but AFTER super.connectedCallback(), which
    //      can itself trigger a style recalculation, latching the animation's
    //      start at 0,0 one moment too early. Symptom, reported by May while
    //      zoomed out (which stretches the frame long enough to see):
    //      "it flies to the left then flies to the right".
    //
    // Setting the properties before super runs means the very first computed
    // style already carries the real coordinates, so there is no corner frame
    // to catch. Both elements are laid out already (the shopper just clicked
    // the button), so getBoundingClientRect is valid immediately.
    if (this.source && this.destination) {
      const sourceRect = this.source.getBoundingClientRect();
      const destinationRect = this.destination.getBoundingClientRect();

      // A zero-sized rect means display:none or detached. Flying to 0,0 would
      // reproduce the original bug, so bail out invisibly instead.
      if (!sourceRect.width || !destinationRect.width) {
        super.connectedCallback();
        this.remove();
        return;
      }

      // Synchronous up to its first await: the setProperty calls land now.
      this.#animate(sourceRect, destinationRect);
    }

    super.connectedCallback();
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    //Define bezier curve points
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    // Position the flying thingy back to the start point
    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    await yieldToMainThread();

    await Promise.allSettled(this.getAnimations().map((a) => a.finished));
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
